/**
 * Consumer Authentication Token Storage
 *
 * Centralized storage utilities for consumer authentication tokens.
 * Standardized to use accessToken/refreshToken for consistency across all portals.
 *
 * Issue #65 Phase 5: Token naming standardization
 *
 * @module lib/auth/storage
 */

// Storage keys - centralized for easy maintenance
// Using standard naming (accessToken/refreshToken) consistent with business portal
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
} as const;

/**
 * Get the access token from localStorage
 * @returns Access token or null if not found
 */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN);
}

/**
 * Get the refresh token from localStorage
 * @returns Refresh token or null if not found
 */
export function getRefreshToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN);
}

/**
 * Store both access and refresh tokens
 * @param accessToken - The JWT access token
 * @param refreshToken - The JWT refresh token
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
}

/**
 * Store only the access token
 * @param accessToken - The JWT access token
 */
export function setAccessToken(accessToken: string): void {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
}

/**
 * Remove all authentication tokens from localStorage
 */
export function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
}

/**
 * Check if user has an access token stored
 * @returns true if access token exists
 */
export function hasAccessToken(): boolean {
  return getAccessToken() !== null;
}
